import { FC, useContext, useEffect, useMemo, useRef } from "react";

import { CommunicationPreferenceFormType, FormType, parseFormBackgroundTheme } from "@simplyk/common";
import dynamic from "next/dynamic";

import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { LiveEditorStepKey } from "../../Steps/LiveEditorStep.enum";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { PushMonthlyContext } from "@/canvas/features/FormV2/blocks/selection/donation-selection/DonationForm/PushMonthly/PushMonthlyContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FormV2Step2 } from "@/canvas/features/FormV2/steps/step2/FormV2Step2";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DeviceSwitchablePreviewLayout } from "@/components/LiveEditorElements/LivePreview/DeviceSwitchablePreviewLayout";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DonationFrontendPreviewProvider } from "@/features/LiveFormEditor/LivePreview/contexts/DonationFrontendPreviewProvider";
import {
  useCommunicationPreferencesForPreview,
  withPreviewQuestionTitle,
} from "@/features/LiveFormEditor/LivePreview/useCommunicationPreferencesForPreview";
import { PreviewLocaleProvider } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewLocaleProvider";

const DonationFormStep1 = dynamic(
  () => import("@/canvas/features/FormV2/steps/step1/Donation/DonationFormStep1").then((mod) => mod.DonationFormStep1),
  {
    ssr: false,
  }
);

const PeerToPeerPrimaryCampaignStep1 = dynamic(
  () =>
    import("@/canvas/features/FormV2/steps/step1/PeerToPeer/PeerToPeerPrimaryCampaignStep1").then(
      (mod) => mod.PeerToPeerPrimaryCampaignStep1
    ),
  {
    ssr: false,
  }
);

const PeerToPeerTeamCampaignStep1 = dynamic(
  () =>
    import("@/canvas/features/FormV2/steps/step1/PeerToPeer/PeerToPeerTeamCampaignStep1").then(
      (mod) => mod.PeerToPeerTeamCampaignStep1
    ),
  {
    ssr: false,
  }
);

const PeerToPeerIndividualCampaignStep1 = dynamic(
  () =>
    import("@/canvas/features/FormV2/steps/step1/PeerToPeer/PeerToPeerIndividualCampaignStep1").then(
      (mod) => mod.PeerToPeerIndividualCampaignStep1
    ),
  {
    ssr: false,
  }
);

const PreviewProvider = dynamic(() => import("../contexts/PreviewContext").then((mod) => mod.PreviewProvider), {
  ssr: false,
});

const FormProviders = dynamic(() => import("@/canvas/features/FormV2/FormProviders").then((mod) => mod.FormProviders), {
  ssr: false,
});

const FormLayout = dynamic(() => import("@/canvas/features/FormV2/FormLayout").then((mod) => mod.FormLayout), {
  ssr: false,
});

const PushMonthlyProvider = dynamic(
  () =>
    import("@/canvas/features/FormV2/blocks/selection/donation-selection/DonationForm/PushMonthly/PushMonthlyContext").then(
      (mod) => mod.PushMonthlyProvider
    ),
  { ssr: false }
);

const PushMonthlyStepHost = dynamic(
  () =>
    import("@/canvas/features/FormV2/blocks/selection/donation-selection/DonationForm/PushMonthly/PushMonthlyStepHost").then(
      (mod) => mod.PushMonthlyStepHost
    ),
  { ssr: false }
);

export const DonationFormLivePreview: FC = () => {
  const { t } = useTranslate();
  const {
    liveFormData,
    liveEditorStepsKeys,
    stepProgress,
    getKeyStepFromIndex,
    currentStepKey,
    currentStepIndex,
    formCategory,
    apiFormObject: donationForm,
    previewDevice,
    setPreviewDevice,
    setPreviewScale,
    iframeRef,
    isOnboarding,
    previewAmountFocus,
    previewStepKey,
    setPreviewStepKey,
  } = useLiveFormEditorContext<"donation-form">();

  const { communicationPreferences, questionTitle } = useCommunicationPreferencesForPreview(
    donationForm.id,
    CommunicationPreferenceFormType.DonationForm
  );
  const previewDonation = useMemo(
    () =>
      liveFormData
        ? {
            ...liveFormData.donation,
            communicationPreferences,
            donationFormFields: withPreviewQuestionTitle(
              liveFormData.donation.donationFormFields,
              donationForm.locale,
              questionTitle
            ),
          }
        : null,
    [liveFormData, communicationPreferences, donationForm.locale, questionTitle]
  );

  const pushMonthlyFlashActiveRef = useRef(false);
  const pushMonthlyTimerRef = useRef<number | undefined>(undefined);
  const prevTriggerRef = useRef(liveFormData?.pushMonthlyPreviewTrigger);

  useEffect(() => {
    if (!liveFormData || pushMonthlyFlashActiveRef.current) {
      return;
    }
    const derivedStep = getPreviewStepKey(
      currentStepKey,
      liveFormData.donation.allowInHonour,
      liveFormData.donation.displayDonorList,
      liveFormData.lastChangedAdvancedField
    );
    setPreviewStepKey(derivedStep);
  }, [
    currentStepKey,
    liveFormData?.donation.allowInHonour,
    liveFormData?.donation.displayDonorList,
    liveFormData?.lastChangedAdvancedField,
    setPreviewStepKey,
    liveFormData,
  ]);

  useEffect(() => {
    const trigger = liveFormData?.pushMonthlyPreviewTrigger;
    const prevTrigger = prevTriggerRef.current;
    prevTriggerRef.current = trigger;

    if (trigger && trigger !== prevTrigger) {
      window.clearTimeout(pushMonthlyTimerRef.current);
      pushMonthlyFlashActiveRef.current = true;
      setPreviewStepKey("pushMonthly");
      pushMonthlyTimerRef.current = window.setTimeout(() => {
        pushMonthlyFlashActiveRef.current = false;
        setPreviewStepKey("selection");
      }, 5000);
    } else if (trigger === 0 && pushMonthlyFlashActiveRef.current) {
      window.clearTimeout(pushMonthlyTimerRef.current);
      pushMonthlyFlashActiveRef.current = false;
      setPreviewStepKey("selection");
    }

    return () => window.clearTimeout(pushMonthlyTimerRef.current);
  }, [liveFormData?.pushMonthlyPreviewTrigger, setPreviewStepKey]);

  if (!liveFormData || !previewDonation) {
    return null;
  }

  const donation = previewDonation;
  const isP2P = formCategory === "PeerToPeerCampaign";

  // Determine campaign type for P2P forms
  const getP2PCampaignComponent = () => {
    if (!isP2P) {
      return <DonationFormStep1 />;
    }

    if (donationForm.isPrimaryCampaign) {
      return <PeerToPeerPrimaryCampaignStep1 />;
    }

    if (donationForm.isPrimaryTeam) {
      return <PeerToPeerTeamCampaignStep1 />;
    }

    return <PeerToPeerIndividualCampaignStep1 />;
  };

  return (
    !!liveFormData && (
      <DeviceSwitchablePreviewLayout
        currentStepIndex={currentStepIndex}
        previewDevice={previewDevice}
        setPreviewDevice={setPreviewDevice}
        setPreviewScale={setPreviewScale}
        iframeRef={iframeRef}
        hasPreviewPadding={previewStepKey === "payment"}
        showConfetti={currentStepKey === "review"}
        slots={{
          content: (
            <PreviewLocaleProvider locale={donationForm.locale}>
              <DonationFrontendPreviewProvider donationForm={donation}>
                <PreviewProvider
                  iframeRef={iframeRef}
                  advancedSettings={{
                    dafEnabled: Boolean(donation.dafEnabled),
                    chequeEnabled: typeof donation.chequeMinimumAmount === "number",
                    allowInHonour: Boolean(donation.allowInHonour),
                  }}
                  isPreview={true}
                  furthestStepReachedIndex={stepProgress.reached}
                  placeholders={liveFormData.placeholders}
                  furthestStepReachedKey={getKeyStepFromIndex(stepProgress.reached)}
                  liveEditorStepsKeys={liveEditorStepsKeys}
                  currentStepKey={currentStepKey}
                  previewAmountFocus={previewAmountFocus ?? null}
                >
                  <FormProviders type={FormType.DonationForm} formObject={donation} isEmbed={false}>
                    <PushMonthlyProvider>
                      <PushMonthlyPreviewSync step={previewStepKey} />
                      <FormLayout
                        step={previewStepKey}
                        slots={{
                          selectionStep: getP2PCampaignComponent(),
                          paymentStep: <FormV2Step2 />,
                          pushMonthlyStep: <PushMonthlyStepHost />,
                          languageSelect: null,
                          closeEmbedButton: null,
                          thankYou: null,
                        }}
                        backgroundTheme={parseFormBackgroundTheme(liveFormData.donation.backgroundTheme)}
                        brandColorHex={liveFormData.donation.color}
                      />
                    </PushMonthlyProvider>
                  </FormProviders>
                </PreviewProvider>
              </DonationFrontendPreviewProvider>
            </PreviewLocaleProvider>
          ),
          footerHint: isOnboarding ? (
            <DashboardTypography variant="body1" vibe="text-neutral-quiet" marginTop={3}>
              {t("dashboard", "allChangesCanBeEditedLater")}
            </DashboardTypography>
          ) : undefined,
        }}
      />
    )
  );
};

const getPreviewStepKey = (
  currentStepKey: LiveEditorStepKey,
  allowInHonour?: boolean,
  displayDonorList?: boolean,
  lastChangedAdvancedField?: string
): FormStepKey => {
  if (currentStepKey === "advanced-settings") {
    if (["dafEnabled", "chequeEnabled", "allowInHonour"].includes(lastChangedAdvancedField ?? "")) {
      return "payment";
    }
    if (lastChangedAdvancedField === "displayDonorList") {
      if (displayDonorList) {
        return "selection";
      }
      // displayDonorList was turned off: show in-honour if active, otherwise show selection (empty donor list)
      return allowInHonour ? "payment" : "selection";
    }
    // Default (initial page load): prioritize showing whichever is enabled
    if (allowInHonour) {
      return "payment";
    }
    return "selection";
  }
  if (["buyer-questions", "thank-you-email", "business-model", "questions"].includes(currentStepKey)) {
    return "payment";
  }
  return "selection";
};

const PREVIEW_ONE_TIME_CENTS = 10000;
const PREVIEW_MONTHLY_CENTS = 2500;

const PushMonthlyPreviewSync: FC<{ step: FormStepKey }> = ({ step }) => {
  const { setState } = useContext(PushMonthlyContext);

  useEffect(() => {
    if (step === "pushMonthly") {
      setState({
        status: "open",
        oneTimeAmountCents: PREVIEW_ONE_TIME_CENTS,
        monthlyAmountCents: PREVIEW_MONTHLY_CENTS,
      });
    }
  }, [step, setState]);

  return null;
};
